import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Trash2, ArrowLeft, GitBranch, Users, Search } from 'lucide-react';
import { AppDispatch, RootState } from '../../store/store';
import {
  fetchDistributionRules,
  createRule,
  updateRule,
  deleteRule,
  selectDistributionRules,
  selectDistributionLoading,
} from '../../store/slices/distributionSlice';
import { DistributionRule, getDistributionOptions } from '../../services/distributionService';
import { getAllIntegration } from '../../services/automationService';
import '../../styles/designSystem.css';
import './leadDistributionSettings.scss';
import logger from '../../utils/logger';

const OPERATORS = [
  { value: 'eq', label: 'Equal' },
  { value: 'ne', label: 'Not Equal' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'lt', label: 'Less Than' },
  { value: 'contain', label: 'Contains' },
  { value: 'doesNotContain', label: 'Does Not Contain' },
];

// Integration IDs for conditional field rendering
const FACEBOOK_INTEGRATION_ID = '64109561e6c7e737227a6384';
const LEAD_FORM_INTEGRATION_ID = '64109561e6c7e737227a6382';

const LeadDistributionSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const rules = useSelector(selectDistributionRules);
  const loading = useSelector(selectDistributionLoading);
  const teamMembers = useSelector((state: RootState) => state.whatsapp.team.members);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<DistributionRule | null>(null);
  const [sources, setSources] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    dispatch(fetchDistributionRules());
    fetchIntegrationSources();
  }, [dispatch]);

  // Fetch all integration sources dynamically (EXACTLY like automation does)
  const fetchIntegrationSources = async () => {
    try {
      logger.log('🔍 Starting integration data fetch...');

      // STEP 1: Make API call with same params as automation
      const response = await getAllIntegration({
        isAscending: false,
        page: 1,
        perPage: 50
      });

      // STEP 2: Check response.status like automation does
      if (response && response.status) {
        logger.log('✅ API response received:', response.data.data.length, 'integrations');

        // STEP 3: Define special sources
        const SPECIAL_SOURCES = ['custom_form', 'lead_form', 'csv'];

        // STEP 4: Apply EXACT same filtering logic as automation
        const filteredIntegrations = response.data.data.filter((integration: any) => {
          const { name, key, isActive, isDisplayable } = integration;

          // Must be active
          if (!isActive) return false;

          // If displayable, include it
          if (isDisplayable) return true;

          // If special source, include it
          if (SPECIAL_SOURCES.includes(key)) {
            logger.log(`🎯 Special source included: ${name}`);
            return true;
          }

          return false;
        });

        // STEP 5: Convert to dropdown format
        const formattedSources = filteredIntegrations.map((integration: any) => ({
          value: integration._id,
          label: integration.name
        }));

        logger.log('📋 Final sources loaded:', formattedSources.length, formattedSources);
        setSources(formattedSources);
      }
    } catch (err) {
      console.error('❌ Integration fetch error:', err);
      // Fallback to basic sources if API fails
      setSources([
        { value: '64109561e6c7e737227a6382', label: 'Lead Form' },
        { value: '64109561e6c7e737227a6384', label: 'Facebook' },
        { value: '64109561e6c7e737227a6383', label: 'IndiaMART' },
        { value: '64109561e6c7e737227a6385', label: 'TradeIndia' },
        { value: '64109561e6c7e737227a637c', label: 'CSV' },
      ]);
    }
  };

  const handleToggleStatus = async (rule: DistributionRule) => {
    await dispatch(updateRule({ ...rule, status: !rule.status }));
    dispatch(fetchDistributionRules());
  };

  const handleDelete = async (ruleId: string) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      await dispatch(deleteRule(ruleId));
      dispatch(fetchDistributionRules());
    }
  };

  const handleEdit = (rule: DistributionRule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleSave = async (rule: DistributionRule) => {
    if (editingRule) {
      await dispatch(updateRule({ ...rule, _id: editingRule._id }));
    } else {
      await dispatch(createRule(rule));
    }
    dispatch(fetchDistributionRules());
    setShowForm(false);
    setEditingRule(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRule(null);
  };

  if (showForm) {
    return <RuleForm onCancel={handleCancel} onSave={handleSave} editRule={editingRule} sources={sources} />;
  }

  return (
    <div className="settings-content-wrapper">
      <div className="ds-flex ds-items-center ds-justify-between" style={{ marginBottom: 'var(--spacing-6)' }}>
        <div>
          <h2 className="ds-h2" style={{ margin: 0, fontFamily: 'var(--font-family-primary)' }}>Lead Distribution Rules</h2>
          <p className="ds-helper-text" style={{ margin: '0.625rem 0 0 0', maxWidth: '640px' }}>
            Automate lead assignment to team members based on source, location, or custom criteria. Create rules to ensure fair distribution and faster response times.
          </p>
        </div>
        <button className="ds-btn ds-btn-primary" onClick={() => setShowForm(true)} style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-semibold)' }}>
          <Plus size={18} />
          Create Rule
        </button>
      </div>

      <div className="ds-flex ds-flex-col ds-gap-4">
        {loading.rules ? (
          <div className="ds-flex ds-items-center ds-justify-center" style={{ padding: '3rem' }}>
            <p className="ds-body ds-text-secondary">Loading rules...</p>
          </div>
        ) : rules.length === 0 ? (
          <div className="ds-card">
            <div className="ds-flex ds-flex-col ds-items-center ds-justify-center" style={{ textAlign: 'center' }}>
              <GitBranch size={64} style={{ color: 'var(--color-neutral-300)', marginBottom: '1.5rem' }} />
              <h3 className="ds-h3" style={{ margin: '0 0 0.5rem 0' }}>No distribution rules</h3>
              <p className="ds-body ds-text-secondary" style={{ margin: '0 0 2rem 0' }}>
                Create rules to automatically distribute leads based on source and criteria
              </p>
              <button className="ds-btn ds-btn-primary" onClick={() => setShowForm(true)}>
                <Plus size={16} />
                Create Your First Rule
              </button>
            </div>
          </div>
        ) : (
          rules.map((rule: DistributionRule) => {
            const assignedMembers = rule.recipientsIds?.ids
              ? Object.keys(rule.recipientsIds.ids).filter(id => rule.recipientsIds!.ids[id].status)
              : [];
            const assignedTeamMembers = teamMembers?.filter((m: any) => assignedMembers.includes(m._id)) || [];

            return (
              <div
                key={rule._id}
                className="ds-card ds-cursor-pointer ds-transition distribution-rule-card"
                onClick={() => handleEdit(rule)}
                style={{
                  marginBottom: '1rem',
                  borderLeft: rule.status ? '4px solid var(--color-success)' : '4px solid var(--color-neutral-300)',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Header Section */}
                <div className="ds-flex ds-justify-between ds-items-start" style={{ marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div className="ds-flex ds-items-center ds-gap-3" style={{ marginBottom: '0.5rem' }}>
                      <h4 className="ds-h4" style={{ margin: 0 }}>{rule.name}</h4>
                      <span className={rule.status ? 'ds-badge ds-badge-success' : 'ds-badge ds-badge-neutral'}>
                        {rule.status ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <div className="ds-flex ds-items-center ds-gap-3" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="ds-btn ds-btn-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(rule._id!);
                      }}
                      title="Delete rule"
                      style={{ color: 'var(--color-error)' }}
                    >
                      <Trash2 size={16} />
                    </button>
                    <label className="switch">
                      <input
                        type="checkbox"
                        checked={rule.status || false}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleToggleStatus(rule);
                        }}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                {/* Rule Preview Section */}
                <div
                  style={{
                    background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)',
                    borderRadius: 'var(--radius-base)',
                    padding: '1rem',
                    marginBottom: '1rem'
                  }}
                >
                  <div className="ds-flex ds-items-start ds-gap-2" style={{ marginBottom: '0.75rem' }}>
                    <GitBranch size={16} style={{ color: 'var(--color-primary)', marginTop: '0.25rem', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <p className="ds-body-sm ds-font-medium" style={{ margin: '0 0 0.5rem 0', color: 'var(--color-primary)' }}>
                        When leads come from
                      </p>
                      <div className="ds-flex ds-items-center ds-gap-2 ds-flex-wrap" style={{ marginBottom: '0.75rem' }}>
                        <span className="ds-badge ds-badge-primary">
                          {sources.find((s: { value: string; label: string }) => s.value === rule.integration)?.label || 'Unknown Source'}
                        </span>
                      </div>

                      {/* Support both old structure (fieldName) and new structure (custom[1].key) */}
                      {(rule.ruleConfig?.fieldName || rule.ruleConfig?.custom?.[1]?.key) && (
                        <>
                          <p className="ds-body-sm ds-font-medium" style={{ margin: '0.75rem 0 0.5rem 0', color: 'var(--color-primary)' }}>
                            {/* Special label for Facebook forms/pages - check both old and new structures */}
                            {(() => {
                              const fieldName = rule.ruleConfig?.fieldName || rule.ruleConfig?.custom?.[1]?.key || '';
                              const isForm = fieldName.toLowerCase() === 'form';
                              const isPage = fieldName.toLowerCase() === 'page';

                              if (rule.integration === FACEBOOK_INTEGRATION_ID && isForm) {
                                return 'Form Name';
                              } else if (rule.integration === FACEBOOK_INTEGRATION_ID && isPage) {
                                return 'Page Name';
                              } else {
                                return `Where ${fieldName}`;
                              }
                            })()}
                          </p>
                          <div className="ds-flex ds-items-center ds-gap-2 ds-flex-wrap">
                            {/* Only show operator for non-Facebook rules - check both old and new structures */}
                            {(() => {
                              const fieldName = rule.ruleConfig?.fieldName || rule.ruleConfig?.custom?.[1]?.key || '';
                              const isFacebookFormOrPage = rule.integration === FACEBOOK_INTEGRATION_ID &&
                                (fieldName.toLowerCase() === 'form' || fieldName.toLowerCase() === 'page');
                              return !isFacebookFormOrPage;
                            })() && (
                              <span className="ds-badge ds-badge-neutral">
                                {OPERATORS.find((o: { value: string; label: string }) => o.value === rule.ruleConfig?.custom?.[1]?.operator)?.label || 'equals'}
                              </span>
                            )}
                            {/* Support both old structure (fieldValue) and new structure (custom[1].value) */}
                            {(rule.ruleConfig?.fieldValue || rule.ruleConfig?.custom?.[1]?.value || '').split(',').filter((v: string) => v.trim()).map((value: string, idx: number) => (
                              <span key={idx} className="ds-badge ds-badge-info" style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {value.trim()}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Team Members & Stats Section */}
                <div className="ds-flex ds-justify-between ds-items-start ds-gap-4" style={{ flexWrap: 'wrap' }}>
                  {/* Assigned Team Members */}
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div className="ds-flex ds-items-center ds-gap-2" style={{ marginBottom: '0.5rem' }}>
                      <Users size={14} style={{ color: 'var(--color-neutral-500)' }} />
                      <p className="ds-body-sm ds-font-medium ds-text-secondary" style={{ margin: 0 }}>
                        Distributes to
                      </p>
                    </div>
                    {assignedTeamMembers.length > 0 ? (
                      <div className="ds-flex ds-items-center ds-gap-2 ds-flex-wrap">
                        {assignedTeamMembers.slice(0, 3).map((member: any) => (
                          <span key={member._id} className="ds-badge ds-badge-success">
                            {member.firstName} {member.lastName}
                          </span>
                        ))}
                        {assignedTeamMembers.length > 3 && (
                          <span className="ds-body-sm ds-text-tertiary" style={{ fontWeight: 'var(--font-weight-medium)' }}>
                            +{assignedTeamMembers.length - 3} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="ds-body-sm ds-text-tertiary" style={{ margin: 0 }}>
                        No team members assigned
                      </p>
                    )}
                  </div>

                  {/* Statistics */}
                  <div style={{ display: 'flex', gap: '2.5rem' }}>
                    <div>
                      <p className="ds-body-sm ds-text-tertiary" style={{ margin: '0 0 0.375rem 0', fontWeight: 'var(--font-weight-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 'var(--font-size-2xs)' }}>
                        Leads Assigned
                      </p>
                      <p className="ds-h4" style={{ margin: 0, color: 'var(--color-primary)', fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-3xl)' }}>
                        {rule.totalDistributed || 0}
                      </p>
                    </div>
                    <div>
                      <p className="ds-body-sm ds-text-tertiary" style={{ margin: '0 0 0.375rem 0', fontWeight: 'var(--font-weight-medium)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 'var(--font-size-2xs)' }}>
                        Number of Runs
                      </p>
                      <p className="ds-h4" style={{ margin: 0, color: 'var(--text-secondary)', fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-3xl)' }}>
                        {rule.totalNoOfRun || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Created By Footer */}
                <div
                  style={{
                    marginTop: '1rem',
                    paddingTop: '0.75rem',
                    borderTop: '1px solid var(--border-light)'
                  }}
                >
                  <p className="ds-body-sm ds-text-tertiary" style={{ margin: 0 }}>
                    Created by <span className="ds-font-medium">{rule.createdBy || 'Unknown'}</span>
                  </p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

interface RuleFormProps {
  onCancel: () => void;
  onSave: (rule: DistributionRule) => void;
  editRule: DistributionRule | null;
  sources: { value: string; label: string }[];
}

const RuleForm: React.FC<RuleFormProps> = ({ onCancel, onSave, editRule, sources }) => {
  const dispatch = useDispatch<AppDispatch>();
  const teamMembers = useSelector((state: RootState) => state.whatsapp.team.members);
  const StoreData = useSelector((state: any) => state.rootReducers);

  // Get custom lead form fields
  const customLeadFormFields = React.useMemo(() => {
    const customForm = StoreData?.userData?.userDetails?.userPreference?.customForm;
    logger.log('📋 Custom form data from Redux:', customForm);

    if (!customForm) {
      logger.log('⚠️ No custom form data found in Redux');
      return [];
    }

    // Filter out specific types (matching mobile logic)
    const filtered = customForm.filter((field: any) =>
      field.type !== 'location' &&
      field.type !== 'date' &&
      field.type !== 'time' &&
      field.type !== 'name' &&
      field.type !== 'email' &&
      field.type !== 'phone'
    );

    logger.log(`✅ Filtered custom lead form fields (${filtered.length}):`, filtered);
    return filtered;
  }, [StoreData?.userData?.userDetails?.userPreference?.customForm]);

  const [formData, setFormData] = useState(() => {
    if (editRule) {
      logger.log('🔍 Loading edit rule:', editRule);
      logger.log('📋 ruleConfig structure:', editRule.ruleConfig);

      const fieldName = editRule.ruleConfig?.fieldName || editRule.ruleConfig?.custom?.[1]?.key || '';
      const fieldValue = editRule.ruleConfig?.fieldValue || editRule.ruleConfig?.custom?.[1]?.value || '';
      const operator = editRule.ruleConfig?.custom?.[1]?.operator || 'eq';

      logger.log('✅ Extracted values:', { fieldName, fieldValue, operator });

      return {
        name: editRule.name || '',
        source: editRule.integration || '',
        fieldName,
        operator,
        values: fieldValue.split(',').filter(v => v.trim()),
        currentValue: '',
      };
    }

    return {
      name: '',
      source: '',
      fieldName: '',
      operator: 'eq',
      values: [],
      currentValue: '',
    };
  });

  const [selectedMembers, setSelectedMembers] = useState<{ [key: string]: boolean }>(
    editRule?.recipientsIds?.ids
      ? Object.keys(editRule.recipientsIds.ids).reduce((acc, id) => {
          acc[id] = editRule.recipientsIds!.ids[id].status;
          return acc;
        }, {} as { [key: string]: boolean })
      : {}
  );

  const [error, setError] = useState<string>('');
  const [teamMemberSearch, setTeamMemberSearch] = useState<string>('');

  // Facebook dynamic fields state
  const [facebookOptions, setFacebookOptions] = useState<{
    pages: Array<{ pageId: string; pageName: string; pageAccessToken: string }>;
    forms: Array<{ id: string; name: string; locale: string; status: string }>;
  } | null>(null);
  const [loadingFacebookOptions, setLoadingFacebookOptions] = useState(false);

  useEffect(() => {
    // Fetch team members if not loaded
    if (!teamMembers || teamMembers.length === 0) {
      dispatch({ type: 'team/fetchMembers' });
    }
  }, [dispatch, teamMembers]);

  // Fetch Facebook options when Facebook is selected as source
  useEffect(() => {
    const fetchFacebookOptions = async () => {
      if (formData.source === FACEBOOK_INTEGRATION_ID) {
        setLoadingFacebookOptions(true);
        try {
          logger.log('🔍 Fetching Facebook distribution options...');
          const response = await getDistributionOptions();

          if (response?.data?.data) {
            logger.log('✅ Facebook options received:', response.data.data);
            setFacebookOptions({
              pages: response.data.data.pages || [],
              forms: response.data.data.forms || []
            });
          }
        } catch (err) {
          logger.error('❌ Failed to fetch Facebook options:', err);
          setFacebookOptions({ pages: [], forms: [] });
        } finally {
          setLoadingFacebookOptions(false);
        }
      } else {
        // Clear Facebook options when other source is selected
        setFacebookOptions(null);
      }
    };

    fetchFacebookOptions();
  }, [formData.source]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form
    const validationMsg = getValidationMessage();
    if (validationMsg) {
      setError(validationMsg);
      return;
    }

    // Clear any existing errors
    setError('');

    const recipientsIds: any = {
      ids: {},
      cursor: editRule?.recipientsIds?.cursor || '',
      current_distribution: editRule?.recipientsIds?.current_distribution || '',
    };

    Object.keys(selectedMembers).forEach((id) => {
      if (selectedMembers[id]) {
        recipientsIds.ids[id] = { waitage: 1, status: true };
      }
    });

    const rule: DistributionRule = {
      name: formData.name,
      integration: formData.source,
      ruleConfig: {
        custom: [
          {
            integration: formData.source,
            users: Object.keys(selectedMembers).filter(id => selectedMembers[id]),
          },
          {
            key: formData.fieldName,
            value: formData.values.join(','),
            operator: formData.operator,
            users: Object.keys(selectedMembers).filter(id => selectedMembers[id]),
          },
        ],
      },
      recipientsIds,
      status: true,
    };

    onSave(rule);
  };

  const addValue = () => {
    if (formData.currentValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        values: [...prev.values, prev.currentValue.trim()],
        currentValue: '',
      }));
    }
  };

  const removeValue = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index),
    }));
  };

  const toggleMember = (memberId: string) => {
    setSelectedMembers((prev) => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  // Validation helper
  const getValidationMessage = (): string => {
    if (!formData.name.trim()) return 'Rule name is required';
    if (!formData.source) return 'Please select a lead source';

    // Facebook-specific validation
    if (formData.source === FACEBOOK_INTEGRATION_ID) {
      if (!formData.fieldName.trim()) {
        return 'Please select either "Form Name" or "Page Name"';
      }
      if (formData.values.length === 0) {
        if (formData.fieldName === 'form') {
          return 'Please select at least one Facebook form';
        } else if (formData.fieldName === 'page') {
          return 'Please select at least one Facebook page';
        }
        return 'Please add at least one value';
      }
    }

    // Lead Form-specific validation
    if (formData.source === LEAD_FORM_INTEGRATION_ID) {
      if (!formData.fieldName.trim()) {
        return 'Please select a field from your lead form';
      }
      if (formData.values.length === 0) {
        return 'Please add at least one value for the selected field';
      }
    }

    // For sources with field configuration (not Calendly)
    const requiresFieldConfig = formData.source !== FACEBOOK_INTEGRATION_ID &&
                                 formData.source !== LEAD_FORM_INTEGRATION_ID &&
                                 formData.source !== '6904d7f673aa0a31f01e4862'; // Calendly

    if (requiresFieldConfig && !formData.fieldName.trim()) {
      return 'Field name is required for this source';
    }

    if (Object.keys(selectedMembers).filter(id => selectedMembers[id]).length === 0) {
      return 'Please select at least one team member to distribute leads to';
    }
    return '';
  };

  const isFormValid = (): boolean => {
    return getValidationMessage() === '';
  };

  // Filter team members based on search
  const filteredTeamMembers = React.useMemo(() => {
    if (!teamMembers) return [];

    const activeMembers = teamMembers.filter((member: any) => member.isActive);

    if (!teamMemberSearch.trim()) return activeMembers;

    const searchLower = teamMemberSearch.toLowerCase();
    return activeMembers.filter((member: any) =>
      `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchLower)
    );
  }, [teamMembers, teamMemberSearch]);

  return (
    <div className="settings-content-wrapper">
      <div className="ds-flex ds-items-center ds-gap-4" style={{ marginBottom: 'var(--spacing-6)' }}>
        <button className="ds-btn ds-btn-ghost" onClick={onCancel} style={{ fontSize: 'var(--font-size-base)', fontWeight: 'var(--font-weight-medium)' }}>
          <ArrowLeft size={20} />
          Back
        </button>
        <h2 className="ds-h2" style={{ margin: 0, fontFamily: 'var(--font-family-primary)' }}>
          {editRule ? 'Edit Distribution Rule' : 'Create Distribution Rule'}
        </h2>
      </div>

      {error && (
        <div className="ds-card" style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          marginBottom: '1.5rem',
          color: 'var(--color-error)'
        }}>
          <p className="ds-body-sm" style={{ margin: 0 }}>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="ds-card">
          <div className="ds-form-group">
            <label className="ds-label">Rule Name*</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              placeholder="Enter rule name"
              className="ds-input"
            />
          </div>

          <div className="ds-form-group">
            <label className="ds-label">Lead Source*</label>
            <select
              value={formData.source}
              onChange={(e) => setFormData((prev) => ({ ...prev, source: e.target.value }))}
              className="ds-select"
            >
              <option value="">Select source</option>
              {sources.map((source: { value: string; label: string }) => (
                <option key={source.value} value={source.value}>
                  {source.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ds-form-group">
            <label className="ds-label">Field Name*</label>
            {/* Facebook - Show Form/Page dropdown */}
            {formData.source === FACEBOOK_INTEGRATION_ID ? (
              <select
                value={formData.fieldName}
                onChange={(e) => setFormData((prev) => ({ ...prev, fieldName: e.target.value, values: [], currentValue: '' }))}
                className="ds-select"
                disabled={loadingFacebookOptions}
              >
                <option value="">Select field</option>
                <option value="form">Form Name</option>
                <option value="page">Page Name</option>
              </select>
            ) : /* Lead Form - Show custom form fields */
            formData.source === LEAD_FORM_INTEGRATION_ID && customLeadFormFields.length > 0 ? (
              <select
                value={formData.fieldName}
                onChange={(e) => setFormData((prev) => ({ ...prev, fieldName: e.target.value, values: [], currentValue: '' }))}
                className="ds-select"
              >
                <option value="">Select field</option>
                {customLeadFormFields.map((field: any) => (
                  <option key={field.value} value={field.value}>
                    {field.name}
                  </option>
                ))}
              </select>
            ) : /* Other sources - Text input */
            (
              <input
                type="text"
                value={formData.fieldName}
                onChange={(e) => setFormData((prev) => ({ ...prev, fieldName: e.target.value }))}
                placeholder="Enter field name"
                className="ds-input"
              />
            )}
          </div>

          <div className="ds-form-group">
            <label className="ds-label">Operator*</label>
            <select
              value={formData.operator}
              onChange={(e) => setFormData((prev) => ({ ...prev, operator: e.target.value }))}
              className="ds-select"
            >
              {OPERATORS.map((op) => (
                <option key={op.value} value={op.value}>
                  {op.label}
                </option>
              ))}
            </select>
          </div>

          <div className="ds-form-group">
            <label className="ds-label">Values*</label>
            {/* Facebook but no field selected - Show disabled state with help text */}
            {formData.source === FACEBOOK_INTEGRATION_ID && !formData.fieldName ? (
              <div className="ds-card" style={{
                background: 'var(--color-neutral-50)',
                border: '1px solid var(--border-light)',
                padding: '1rem',
                textAlign: 'center'
              }}>
                <p className="ds-body-sm ds-text-secondary" style={{ margin: 0 }}>
                  Please select "Form Name" or "Page Name" in the field above first
                </p>
              </div>
            ) : /* Lead Form with field options - Show options dropdown */
            formData.source === LEAD_FORM_INTEGRATION_ID && formData.fieldName &&
             customLeadFormFields.find((f: any) => f.value === formData.fieldName)?.options?.length ? (
              <>
                <select
                  value={formData.currentValue}
                  onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                  className="ds-select"
                  style={{ marginBottom: '0.75rem' }}
                >
                  <option value="">Select value</option>
                  {customLeadFormFields
                    .find((f: any) => f.value === formData.fieldName)
                    ?.options?.map((option: string) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className="ds-btn ds-btn-primary"
                  onClick={addValue}
                  disabled={!formData.currentValue}
                  style={{ marginBottom: '0.75rem' }}
                >
                  Add
                </button>
              </>
            ) : /* Facebook Form Name - Show forms dropdown */
            formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'form' ? (
              <>
                <select
                  value={formData.currentValue}
                  onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                  className="ds-select"
                  disabled={loadingFacebookOptions || !facebookOptions}
                  style={{ marginBottom: '0.75rem' }}
                >
                  <option value="">
                    {loadingFacebookOptions ? 'Loading forms...' : 'Select Facebook form'}
                  </option>
                  {facebookOptions?.forms?.map((form) => (
                    <option key={form.id} value={`${form.name} (${form.id})`}>
                      {form.name} ({form.id})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="ds-btn ds-btn-primary"
                  onClick={addValue}
                  disabled={!formData.currentValue}
                  style={{ marginBottom: '0.75rem' }}
                >
                  Add Form
                </button>
              </>
            ) : /* Facebook Page Name - Show pages dropdown */
            formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'page' ? (
              <>
                <select
                  value={formData.currentValue}
                  onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                  className="ds-select"
                  disabled={loadingFacebookOptions || !facebookOptions}
                  style={{ marginBottom: '0.75rem' }}
                >
                  <option value="">
                    {loadingFacebookOptions ? 'Loading pages...' : 'Select Facebook page'}
                  </option>
                  {facebookOptions?.pages?.map((page) => (
                    <option key={page.pageId} value={`${page.pageName} (${page.pageId})`}>
                      {page.pageName} ({page.pageId})
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="ds-btn ds-btn-primary"
                  onClick={addValue}
                  disabled={!formData.currentValue}
                  style={{ marginBottom: '0.75rem' }}
                >
                  Add Page
                </button>
              </>
            ) : /* Regular text input for other sources */
            (
              <div className="ds-flex ds-gap-3" style={{ marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  value={formData.currentValue}
                  onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                  placeholder="Type and press Add"
                  className="ds-input"
                  style={{ flex: 1 }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addValue();
                    }
                  }}
                />
                <button type="button" className="ds-btn ds-btn-primary" onClick={addValue}>
                  Add
                </button>
              </div>
            )}

            {/* Display added values as badges */}
            <div className="ds-flex ds-flex-wrap ds-gap-2">
              {formData.values.map((value, index) => (
                <span key={index} className="ds-badge ds-badge-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem' }}>
                  {value}
                  <button
                    type="button"
                    onClick={() => removeValue(index)}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'inherit',
                      fontSize: '1.25rem',
                      cursor: 'pointer',
                      padding: 0,
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="ds-form-group">
            <label className="ds-label">Distribute to Team Members*</label>

            {/* Search Box */}
            {teamMembers && teamMembers.filter((m: any) => m.isActive).length > 0 && (
              <div style={{ position: 'relative', marginBottom: '0.75rem' }}>
                <input
                  type="text"
                  value={teamMemberSearch}
                  onChange={(e) => setTeamMemberSearch(e.target.value)}
                  placeholder="Search team members..."
                  className="ds-input"
                  style={{ paddingLeft: '2.5rem' }}
                />
                <Search
                  size={16}
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-neutral-400)'
                  }}
                />
              </div>
            )}

            <div style={{
              maxHeight: '300px',
              overflowY: 'auto',
              border: '1px solid var(--border-light)',
              borderRadius: 'var(--radius-base)',
              padding: '0.75rem',
              background: 'var(--bg-primary)'
            }}>
              {!teamMembers || teamMembers.filter((m: any) => m.isActive).length === 0 ? (
                /* Empty State - No Team Members */
                <div className="ds-flex ds-flex-col ds-items-center ds-justify-center" style={{ padding: '2rem', textAlign: 'center' }}>
                  <Users size={48} style={{ color: 'var(--color-neutral-300)', marginBottom: '1rem' }} />
                  <h4 className="ds-h5" style={{ margin: '0 0 0.5rem 0' }}>No Team Members</h4>
                  <p className="ds-body-sm ds-text-tertiary" style={{ margin: 0, maxWidth: '300px' }}>
                    Add team members to your organization to distribute leads automatically.
                  </p>
                </div>
              ) : filteredTeamMembers.length === 0 ? (
                /* Empty State - No Search Results */
                <div className="ds-flex ds-flex-col ds-items-center ds-justify-center" style={{ padding: '2rem', textAlign: 'center' }}>
                  <Search size={48} style={{ color: 'var(--color-neutral-300)', marginBottom: '1rem' }} />
                  <h4 className="ds-h5" style={{ margin: '0 0 0.5rem 0' }}>No members found</h4>
                  <p className="ds-body-sm ds-text-tertiary" style={{ margin: 0 }}>
                    Try a different search term
                  </p>
                </div>
              ) : (
                /* Team Members List */
                <div className="ds-flex ds-flex-col ds-gap-2">
                  {filteredTeamMembers.map((member: any) => (
                    <label
                      key={member._id}
                      className="ds-flex ds-items-center ds-gap-3 ds-cursor-pointer ds-transition team-member-checkbox-label"
                      style={{
                        padding: '0.5rem',
                        borderRadius: 'var(--radius-base)'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers[member._id] || false}
                        onChange={() => toggleMember(member._id)}
                        style={{ width: '1.125rem', height: '1.125rem', cursor: 'pointer' }}
                      />
                      <span className="ds-body-sm">
                        {member.firstName} {member.lastName}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Rule Preview - Only show when form is ready */}
          {(formData.source || formData.fieldName || formData.values.length > 0) && (() => {
            // Debug logging
            logger.log('🔍 Rule Preview - formData.values:', formData.values);
            logger.log('🔍 Rule Preview - values.length:', formData.values.length);
            logger.log('🔍 Rule Preview - formData:', formData);

            return (
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 'var(--radius-lg)',
                padding: '1.5rem',
                marginTop: '1.5rem',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}>
                <div className="ds-flex ds-items-center ds-gap-2" style={{ marginBottom: '0.75rem' }}>
                  <GitBranch size={20} style={{ color: '#fff' }} />
                  <h6 className="ds-body ds-font-semibold" style={{ margin: 0, color: '#fff' }}>Rule Preview</h6>
                </div>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.15)',
                  borderRadius: 'var(--radius-base)',
                  padding: '1rem',
                  backdropFilter: 'blur(10px)'
                }}>
                  <p className="ds-body-sm" style={{ margin: 0, color: '#fff', lineHeight: 1.6, fontFamily: 'var(--font-family-primary)' }}>
                    <span style={{ opacity: 0.9 }}>When lead source is</span>{' '}
                    <strong style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '0.125rem 0.5rem', borderRadius: '4px' }}>
                      {sources.find((s: { value: string; label: string }) => s.value === formData.source)?.label || 'not selected'}
                    </strong>
                    {formData.fieldName && (
                      <>
                        {' '}<span style={{ opacity: 0.9 }}>and</span>{' '}
                        <strong style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '0.125rem 0.5rem', borderRadius: '4px' }}>
                          {/* Show "Form Name" or "Page Name" for Facebook */}
                          {formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'form'
                            ? 'Form Name'
                            : formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'page'
                            ? 'Page Name'
                            : formData.fieldName}
                        </strong>
                        {/* Only show operator for non-Facebook rules */}
                        {!(formData.source === FACEBOOK_INTEGRATION_ID && (formData.fieldName === 'form' || formData.fieldName === 'page')) && (
                          <>
                            {' '}
                            <strong style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '0.125rem 0.5rem', borderRadius: '4px' }}>
                              {OPERATORS.find((o: { value: string; label: string }) => o.value === formData.operator)?.label?.toLowerCase() || 'equals'}
                            </strong>
                          </>
                        )}
                      </>
                    )}
                    {formData.values && formData.values.length > 0 ? (
                      <>
                        {' '}<span style={{ opacity: 0.9 }}>
                          {formData.source === FACEBOOK_INTEGRATION_ID
                            ? 'is'
                            : formData.source === LEAD_FORM_INTEGRATION_ID
                            ? 'is'
                            : 'to'}
                        </span>{' '}
                        <strong style={{ background: 'rgba(255, 255, 255, 0.3)', padding: '0.125rem 0.5rem', borderRadius: '4px', maxWidth: '250px', display: 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'middle' }}>
                          {formData.values.length > 1
                            ? `${formData.values[0]?.trim() || ''} +${formData.values.length - 1} more`
                            : formData.values[0]?.trim() || ''}
                        </strong>
                      </>
                    ) : formData.fieldName && (formData.source === FACEBOOK_INTEGRATION_ID || formData.source === LEAD_FORM_INTEGRATION_ID) ? (
                      <>
                        {' '}<span style={{ opacity: 0.9 }}>is</span>{' '}
                        <span style={{ opacity: 0.7, fontStyle: 'italic' }}>(select values below)</span>
                      </>
                    ) : null}
                    {Object.keys(selectedMembers).filter(id => selectedMembers[id]).length > 0 && (
                      <>
                        <br />
                        <span style={{ opacity: 0.9 }}>Distribute to:</span>{' '}
                        <strong style={{ background: 'rgba(255, 255, 255, 0.2)', padding: '0.125rem 0.5rem', borderRadius: '4px' }}>
                          {Object.keys(selectedMembers).filter(id => selectedMembers[id]).length} team member{Object.keys(selectedMembers).filter(id => selectedMembers[id]).length !== 1 ? 's' : ''}
                        </strong>
                      </>
                    )}
                  </p>
                </div>
              </div>
            );
          })()}
        </div>

        <div className="ds-flex ds-justify-end ds-gap-3" style={{ marginTop: '1.5rem' }}>
          <button type="button" className="ds-btn ds-btn-outline" onClick={onCancel}>
            Cancel
          </button>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              type="submit"
              className="ds-btn ds-btn-primary"
              disabled={!isFormValid()}
              title={!isFormValid() ? getValidationMessage() : ''}
              style={{
                cursor: !isFormValid() ? 'not-allowed' : 'pointer',
                opacity: !isFormValid() ? 0.6 : 1
              }}
            >
              {editRule ? 'Update Rule' : 'Save Rule'}
            </button>
            {!isFormValid() && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '100%',
                  right: 0,
                  marginBottom: '0.5rem',
                  background: 'var(--color-error)',
                  color: '#fff',
                  padding: '0.5rem 0.75rem',
                  borderRadius: 'var(--radius-base)',
                  fontSize: '0.75rem',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  pointerEvents: 'none',
                  opacity: 0,
                  transition: 'opacity 0.2s ease'
                }}
                className="validation-tooltip"
              >
                {getValidationMessage()}
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '1rem',
                    width: 0,
                    height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid var(--color-error)'
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default LeadDistributionSettings;